import ChatIcon from '@mui/icons-material/Chat';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HandymanIcon from '@mui/icons-material/Handyman';
import SignpostIcon from '@mui/icons-material/Signpost';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import { IconButton, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { FunnelFunctions, NodeVariants } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Node } from 'reactflow';
import useCampaignNodeStore from 'stores/campaign-node-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import PromptManager from './prompt-manager';

interface Props {
  productGroup: ProductGroups;
  node: Node;
  campaignId: string;
  loading: boolean;
  linkedinAccount: LinkedinAccount | undefined;
  customPlaceholders: import('../../campaign-builder.types').CustomPlaceholder[];
  fieldInUse: { [key: string]: boolean };

  handleSelectNodeFromList(): void;

  handleUnselectNodeFromList(): void;

  handleOpenUnableToGenerateContactRequestDialog(): void;
}

const NodeItem: React.FC<Props> = ({
  productGroup,
  node,
  campaignId,
  loading,
  linkedinAccount,
  customPlaceholders,
  fieldInUse,
  handleSelectNodeFromList,
  handleUnselectNodeFromList,
  handleOpenUnableToGenerateContactRequestDialog,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const { selectedNode } = useCampaignNodeStore((state) => ({
    selectedNode: state.selectedNode,
  }));

  const { type, data } = node;
  const { functionName, functionNumber } = data;

  const isSelected = !_.isNil(selectedNode) && selectedNode.id === node.id;

  /**
   * Choose which icon to render based on the node type
   */
  const getPrimaryNodeIcon = () => {
    switch (type) {
      case NodeVariants.CONDITIONAL_NODE:
      case NodeVariants.CONDITION_WITH_DURATION_NODE:
        return <SignpostIcon className={classes.nodeHeadIcon} />;
      case NodeVariants.FUNCTION_NODE:
        return (
          <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ position: 'relative' }}>
            <ChatBubbleIcon className={cx(classes.nodeHeadIcon, isSelected ? 'selected' : undefined)} />
            {!_.isNil(functionNumber) ? <Typography className={classes.iconNumber}>{functionNumber}</Typography> : null}
          </Stack>
        );
      case NodeVariants.WAIT_NODE:
        return <WatchLaterIcon className={classes.nodeHeadIcon} />;
      case NodeVariants.UTILITY_NODE:
        return <HandymanIcon className={classes.nodeHeadIcon} />;
      default:
        return <ChatIcon className={classes.nodeHeadIcon} />;
    }
  };

  /**
   * Return a localized label for each known functionName
   */
  const getFunctionName = () => {
    switch (functionName) {
      case FunnelFunctions.CHECK_IF_CONNECTED:
        return t('connectedOnLinkedIn');
      case FunnelFunctions.CHECK_IF_CONNECTED_UNTIL:
        return t('contactRequestAccepted');
      case FunnelFunctions.CHECK_IF_GENERATED_EMAIL_ADDRESS:
        return t('hasEmailAddress');
      case FunnelFunctions.CHECK_IS_POLITE:
        return t('politeSpeechQuestion', { defaultValue: t('politeSpeech') + '?' });
      case FunnelFunctions.WAIT_STEP:
        return t('waitingTime');
      case FunnelFunctions.SEND_CALL_TRANSCRIPT:
        return t('sendCallTranscript'); // Make sure you have a translation key "sendCallTranscript"

      default:
        // If there's no custom case, fall back to the raw function name
        return t(functionName);
    }
  };

  return (
    <div className={cx(classes.nodeItem, isSelected ? 'selected' : classes.unselected)}>
      <Stack
        className={classes.nodeItemHead}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        onClick={!isSelected ? handleSelectNodeFromList : handleUnselectNodeFromList}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          {getPrimaryNodeIcon()}
          <Typography className={cx(classes.nodeHeadText, isSelected ? 'selected' : undefined)}>
            {functionName ? getFunctionName() : t('noActionSelected')}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" gap={2}>
          <Typography className={classes.actionText}>{t('editPrompt')}</Typography>
          <IconButton className={cx(classes.expandButton, isSelected ? 'selected' : undefined)} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Stack>

      <PromptManager
        productGroup={productGroup}
        isSelected={isSelected}
        campaignId={campaignId}
        node={selectedNode}
        loading={loading}
        linkedinAccount={linkedinAccount}
        customPlaceholders={customPlaceholders}
        fieldInUse={fieldInUse}
        handleOpenUnableToGenerateContactRequestDialog={handleOpenUnableToGenerateContactRequestDialog}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  nodeItem: {
    width: '100%',
    background: '#FFF',
    borderRadius: '8px',
    '&.selected': {
      boxShadow: Shadows.GREY_SHADOW_LIGHTER,
    },
  },
  unselected: {
    '&:hover': {
      '& .MuiTypography-root': {
        color: theme.palette.primary.main,
      },
      '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
      },
    },
  },
  nodeItemHead: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
    padding: '8px 16px',
    cursor: 'pointer',
  },
  nodeHeadIcon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.greys.medium,
    '&.selected': {
      color: theme.palette.primary.main,
    },
  },
  iconNumber: {
    position: 'absolute',
    top: 'calc(50% - 2px)',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: theme.typography.pxToRem(11),
    lineHeight: 1,
    color: '#FFF !important',
  },
  nodeHeadText: {
    fontSize: theme.typography.pxToRem(14),
    marginBottom: '2px',
    '&.selected': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  regularIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
  expandButton: {
    pointerEvents: 'none',
    padding: '2px',
    '& .MuiSvgIcon-root': {
      transition: '225ms transform',
      fontSize: theme.typography.pxToRem(16),
    },
    '&.selected': {
      '& .MuiSvgIcon-root': {
        transform: 'rotate(-90deg)',
      },
    },
  },
  actionText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
}));

export default NodeItem;
